import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Filter, Loader2, RefreshCw, ScrollText, Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTION_LABELS = {
  lead_registered: { label: 'Lead registrato', cls: 'bg-sky-50 border-sky-200 text-sky-700' },
  crm_upsert: { label: 'CRM upsert', cls: 'bg-teal-50 border-teal-200 text-teal-700' },
  sheet_sync: { label: 'Sync Sheet', cls: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
  sheet_sync_retry: { label: 'Retry Sheet', cls: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
  sheet_sync_push_all: { label: 'Push-all Sheet', cls: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
  sheet_sync_test: { label: 'Test Sheet', cls: 'bg-slate-50 border-slate-200 text-slate-600' },
  delete_records: { label: 'Cancellazione manuale', cls: 'bg-red-50 border-red-200 text-red-600' },
  webinar_toggle: { label: 'Iscrizioni webinar', cls: 'bg-violet-50 border-violet-200 text-violet-700' },
  webinar_link_updated: { label: 'Link webinar', cls: 'bg-orange-50 border-orange-200 text-orange-600' },
  webinar_reminder: { label: 'Reminder webinar', cls: 'bg-orange-50 border-orange-200 text-orange-600' },
};

const RESULT_STYLES = {
  ok: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  synced: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  queued: 'bg-amber-50 border-amber-200 text-amber-700',
  partial: 'bg-amber-50 border-amber-200 text-amber-700',
  failed: 'bg-red-50 border-red-200 text-red-600',
  skipped_no_link: 'bg-amber-50 border-amber-200 text-amber-700',
};

export const AuditPanel = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState('');
  const [result, setResult] = useState('');
  const [q, setQ] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const headers = useMemo(() => ({ 'X-Admin-Token': token }), [token]);

  const fetchLogs = useCallback(async (filters = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.action) params.set('action', filters.action);
      if (filters.result) params.set('result', filters.result);
      if (filters.q) params.set('q', filters.q);
      if (filters.dateFrom) params.set('date_from', filters.dateFrom);
      if (filters.dateTo) params.set('date_to', filters.dateTo);
      params.set('limit', '200');
      const r = await axios.get(`${API}/admin/audit-logs?${params.toString()}`, { headers });
      setData(r.data);
    } catch {
      toast.error('Errore nel caricamento dello storico audit');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const apply = () => fetchLogs({ action, result, q, dateFrom, dateTo });
  const reset = () => {
    setAction(''); setResult(''); setQ(''); setDateFrom(''); setDateTo('');
    fetchLogs();
  };

  const events = data?.events || [];
  const results = useMemo(() => {
    const s = new Set(events.map((e) => e.result).filter(Boolean));
    ['ok', 'failed', 'partial', 'queued', 'synced'].forEach((r) => s.add(r));
    return [...s].sort();
  }, [events]);

  return (
    <div className="space-y-5" data-testid="pv-admin-audit-panel">
      <div className="bg-white border border-slate-200 rounded-2xl p-5 md:p-6">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="shrink-0 w-11 h-11 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center">
            <ScrollText className="w-5 h-5 text-slate-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-1">
              Storico audit · append-only
            </div>
            <h3 className="font-heading font-bold text-pv-ink text-[16px]">
              Cronologia completa: registrazioni, sync CRM/Sheet, reminder, cancellazioni
            </h3>
            <p className="text-[12.5px] text-slate-600 mt-1">
              {data ? `${data.total} eventi totali · ${data.count} mostrati` : '—'} · Lo storico non viene mai cancellato automaticamente.
            </p>
          </div>
          <button onClick={() => fetchLogs({ action, result, q, dateFrom, dateTo })} className="pv-btn-ghost !py-2 !px-4 !text-[12px]" data-testid="pv-admin-audit-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Aggiorna
          </button>
        </div>

        {/* Filters */}
        <div className="mt-4 pt-4 border-t border-slate-100 flex items-end gap-3 flex-wrap">
          <div>
            <label className="block font-mono text-[9.5px] uppercase tracking-[0.2em] text-slate-500 font-bold mb-1.5">Azione</label>
            <select
              value={action}
              onChange={(e) => setAction(e.target.value)}
              data-testid="pv-admin-audit-filter-action"
              className="px-3 py-2 rounded-xl bg-white border border-slate-200 text-[12.5px] font-semibold text-slate-700 focus:outline-none focus:border-pv-teal/60"
            >
              <option value="">Tutte</option>
              {(data?.actions || []).map((a) => (
                <option key={a} value={a}>{ACTION_LABELS[a]?.label || a}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block font-mono text-[9.5px] uppercase tracking-[0.2em] text-slate-500 font-bold mb-1.5">Esito</label>
            <select
              value={result}
              onChange={(e) => setResult(e.target.value)}
              data-testid="pv-admin-audit-filter-result"
              className="px-3 py-2 rounded-xl bg-white border border-slate-200 text-[12.5px] font-semibold text-slate-700 focus:outline-none focus:border-pv-teal/60"
            >
              <option value="">Tutti</option>
              {results.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="block font-mono text-[9.5px] uppercase tracking-[0.2em] text-slate-500 font-bold mb-1.5">Email / telefono / testo</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && apply()}
                placeholder="mario@rossi.it, +39…"
                data-testid="pv-admin-audit-filter-q"
                className="pl-9 pr-3 py-2 rounded-xl bg-white border border-slate-200 text-[12.5px] text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-pv-teal/60 w-52"
              />
            </div>
          </div>
          <div>
            <label className="block font-mono text-[9.5px] uppercase tracking-[0.2em] text-slate-500 font-bold mb-1.5">Dal</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="pv-admin-audit-filter-from"
              className="px-3 py-2 rounded-xl bg-white border border-slate-200 text-[12.5px] text-slate-700 focus:outline-none focus:border-pv-teal/60" />
          </div>
          <div>
            <label className="block font-mono text-[9.5px] uppercase tracking-[0.2em] text-slate-500 font-bold mb-1.5">Al</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="pv-admin-audit-filter-to"
              className="px-3 py-2 rounded-xl bg-white border border-slate-200 text-[12.5px] text-slate-700 focus:outline-none focus:border-pv-teal/60" />
          </div>
          <button onClick={apply} data-testid="pv-admin-audit-filter-apply"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-pv-ink hover:bg-slate-800 text-white text-[12.5px] font-bold transition-colors">
            <Filter className="w-3.5 h-3.5" /> Filtra
          </button>
          <button onClick={reset} data-testid="pv-admin-audit-filter-reset" className="pv-btn-ghost !py-2 !px-3 !text-[12px]">
            Reset
          </button>
        </div>
      </div>

      {/* Events table */}
      {loading && events.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 text-slate-500 animate-spin" />
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-slate-200">
          <p className="text-slate-500 font-semibold">Nessun evento audit con questi filtri</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="text-left font-mono text-[9.5px] uppercase tracking-[0.18em] text-slate-500 border-b border-slate-100">
                <th className="px-4 py-3">Data / ora</th>
                <th className="px-4 py-3">Azione</th>
                <th className="px-4 py-3">Dettaglio</th>
                <th className="px-4 py-3">Esito</th>
              </tr>
            </thead>
            <tbody>
              {events.map((ev) => {
                const al = ACTION_LABELS[ev.action] || { label: ev.action, cls: 'bg-slate-50 border-slate-200 text-slate-600' };
                const rs = RESULT_STYLES[ev.result] || 'bg-slate-50 border-slate-200 text-slate-500';
                return (
                  <tr key={ev.id} className="border-b border-slate-50 hover:bg-slate-50/60 align-top" data-testid={`pv-admin-audit-row-${ev.id}`}>
                    <td className="px-4 py-3 font-mono text-[11px] text-slate-500 whitespace-nowrap">{formatDate(ev.timestamp)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full border text-[10.5px] font-bold whitespace-nowrap ${al.cls}`}>
                        {al.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-700 max-w-md">
                      <EventDetail ev={ev} />
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full border text-[10.5px] font-bold ${rs}`}>
                        {ev.result || 'ok'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const EventDetail = ({ ev }) => {
  const bits = [];
  if (ev.email) bits.push(ev.email);
  if (ev.phone) bits.push(ev.phone);
  if (ev.key_value && !ev.email) bits.push(ev.key_value);
  if (ev.origin) bits.push(`origine: ${ev.origin}`);
  if (ev.tab) bits.push(`tab: ${ev.tab}`);
  if (ev.crm_action) bits.push(`azione CRM: ${ev.crm_action}`);
  if (ev.collection_label || ev.collection) bits.push(ev.collection_label || ev.collection);
  if (typeof ev.deleted_count === 'number') bits.push(`${ev.deleted_count} record eliminati`);
  if (Array.isArray(ev.deleted_records) && ev.deleted_records.length > 0) {
    bits.push(ev.deleted_records.map((d) => d.email || d.phone || d.id).filter(Boolean).slice(0, 3).join(', '));
  }
  if (typeof ev.sent === 'number') bits.push(`inviati: ${ev.sent} · falliti: ${ev.failed ?? 0} · saltati: ${ev.skipped ?? 0}`);
  if (typeof ev.synced === 'number') bits.push(`synced: ${ev.synced} · failed: ${ev.failed ?? 0}`);
  if (ev.triggered_by) bits.push(`da: ${ev.triggered_by}`);
  if (ev.detail) bits.push(ev.detail);
  if (ev.reason) bits.push(`motivo: ${ev.reason}`);
  if (ev.ip) bits.push(`IP: ${ev.ip}`);
  return <span className="leading-relaxed break-words">{bits.join(' · ') || '—'}</span>;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return iso;
  }
};

export default AuditPanel;
